import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import type { AuditEventView } from '@traceio-org/platform-client';
import { EmptyPageState, NavigationGlyph } from '@traceio-org/ui';
import { AUDIT_PAGE_SIZE } from '../api';
import { useAdmin } from '../context';
import { endOfDay, formatAuditValue, formatCount, isDay, plural, startOfDay } from '../format';
import { useAdminRead } from '../useAdminRead';
import { previousCursors } from '../pages/OrganizationsPage';
import { ActionLink, AdminProblem } from './AdminProblem';
import { LoadingBlock, PagerButton } from './feedback';
import { TextField } from './fields';
import { DateTimeText, ScrollTable } from './records';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The filters of an audit view, as the address keeps them. */
export interface AuditFilters {
  action: string;
  actor: string;
  from: string;
  to: string;
  organization: string;
}

const FILTER_PARAMS: Readonly<Record<keyof AuditFilters, string>> = {
  action: 'action',
  actor: 'actor',
  from: 'from',
  to: 'to',
  organization: 'org',
};

export function auditSearch(filters: AuditFilters, cursor?: string): string {
  const params = new URLSearchParams();
  for (const [field, param] of Object.entries(FILTER_PARAMS) as Array<[keyof AuditFilters, string]>) {
    const value = filters[field].trim();
    if (value) params.set(param, value);
  }
  if (cursor) params.set('cursor', cursor);
  const search = params.toString();
  return search ? `?${search}` : '';
}

function filtersFrom(params: URLSearchParams): AuditFilters {
  return {
    action: params.get('action') ?? '',
    actor: params.get('actor') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    organization: params.get('org') ?? '',
  };
}

type FilterErrors = Partial<Record<keyof AuditFilters, string>>;

function validateFilters(filters: AuditFilters): FilterErrors {
  const errors: FilterErrors = {};
  if (filters.action.trim().length > 100) errors.action = 'An action has at most 100 characters.';
  if (filters.actor.trim().length > 100) errors.actor = 'An actor ID has at most 100 characters.';
  if (filters.from && !isDay(filters.from)) errors.from = 'Enter a date.';
  if (filters.to && !isDay(filters.to)) errors.to = 'Enter a date.';
  if (!errors.from && !errors.to && filters.from && filters.to && filters.to < filters.from) {
    errors.to = 'The last day cannot be before the first.';
  }
  if (filters.organization.trim() && !UUID.test(filters.organization.trim())) {
    errors.organization = 'An organization ID looks like 0190a000-0000-7000-8000-000000000001.';
  }
  return errors;
}

const ACTOR_TYPES: Readonly<Record<string, string>> = {
  user: 'Member',
  staff: 'Trace staff',
  system: 'Trace',
  service: 'Service',
};

function Actor({ event }: { event: AuditEventView }) {
  const type = ACTOR_TYPES[event.actor.type] ?? event.actor.type;
  return (
    <>
      {event.actor.displayName ?? type}
      <span className="admin-cell-secondary">
        {event.actor.displayName ? type : null}
        {event.actor.id ? (
          <>
            {event.actor.displayName ? ' ' : null}
            <code>{event.actor.id}</code>
          </>
        ) : null}
      </span>
    </>
  );
}

/** The field-level changes of an event, already redacted by platform-api, shown as text only. */
function Changes({ event }: { event: AuditEventView }) {
  if (event.changes.length === 0) return <span className="admin-muted">None</span>;
  return (
    <details className="admin-details">
      <summary>{plural(event.changes.length, 'change', 'changes')}</summary>
      <ul className="admin-changes">
        {event.changes.map((change, index) => (
          <li key={`${change.field}:${index}`}>
            <code>{change.field}</code>
            <span className="admin-changes__values">
              <span>
                <span className="admin-sr-only">Before: </span>
                {formatAuditValue(change.before)}
              </span>
              <span aria-hidden="true"> → </span>
              <span>
                <span className="admin-sr-only">, after: </span>
                {formatAuditValue(change.after)}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}

function AuditTable({
  events,
  caption,
  busy,
  showOrganization,
}: {
  events: readonly AuditEventView[];
  caption: string;
  busy: boolean;
  showOrganization: boolean;
}) {
  return (
    <ScrollTable caption={caption} busy={busy} className="admin-table admin-table--audit">
      <thead>
        <tr>
          <th scope="col">When</th>
          <th scope="col">Actor</th>
          <th scope="col">Action</th>
          {showOrganization ? <th scope="col">Organization</th> : null}
          <th scope="col">Target</th>
          <th scope="col">Reason</th>
          <th scope="col">Changes</th>
          <th scope="col">Reference</th>
        </tr>
      </thead>
      <tbody>
        {events.map((event) => (
          <tr key={event.id}>
            <th scope="row">
              <DateTimeText iso={event.occurredAt} />
            </th>
            <td>
              <Actor event={event} />
            </td>
            <td>
              <code>{event.action}</code>
            </td>
            {showOrganization ? (
              <td>
                {event.organizationId ? (
                  <Link className="admin-link" to={`/organizations/${event.organizationId}`}>
                    <code>{event.organizationId}</code>
                  </Link>
                ) : (
                  <span className="admin-muted">None</span>
                )}
              </td>
            ) : null}
            <td>
              {event.target.type}
              {event.target.id ? (
                <span className="admin-cell-secondary">
                  <code>{event.target.id}</code>
                </span>
              ) : null}
            </td>
            <td className="admin-cell-wrap">{event.reason ?? <span className="admin-muted">None</span>}</td>
            <td>
              <Changes event={event} />
            </td>
            <td>
              {event.correlationId ? (
                <code>{event.correlationId}</code>
              ) : (
                <span className="admin-muted">None</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </ScrollTable>
  );
}

/**
 * An audit log view: platform-api's audit events newest first, 50 per keyset page, filtered by action, actor,
 * dates (UTC days) and, on the global page, organization. The filters and the page live in the address, so
 * a filtered view can be shared. `organizationId` fixes the organization (an organization's Audit section);
 * `supportAccess` words a refusal that may come from missing support access.
 */
export function AuditLog({
  organizationId,
  supportAccess,
}: {
  organizationId?: string;
  supportAccess?: { canRequest: boolean; requestPath: string } | undefined;
}) {
  const { api } = useAdmin();
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const applied = filtersFrom(params);
  const appliedSearch = auditSearch(applied);
  const cursor = params.get('cursor') || undefined;
  const previous = cursor ? previousCursors(location.state) : [];
  const pageNumber = previous ? previous.length + 1 : null;
  const backSearch = (location.state as { listSearch?: unknown } | null)?.listSearch;
  const carried = typeof backSearch === 'string' ? { listSearch: backSearch } : {};
  const global = organizationId === undefined;

  const [draft, setDraft] = useState<AuditFilters>(applied);
  const [errors, setErrors] = useState<FilterErrors>({});
  // The fields follow the address when it changes from elsewhere (back and forward, a cleared filter).
  useEffect(() => {
    setDraft(filtersFrom(new URLSearchParams(appliedSearch)));
    setErrors({});
  }, [appliedSearch]);

  const appliedErrors = validateFilters(applied);
  const valid = Object.keys(appliedErrors).length === 0;
  const scopeOrganization = organizationId ?? (applied.organization.trim() || undefined);
  const key = valid
    ? `audit\u0000${scopeOrganization ?? ''}\u0000${appliedSearch}\u0000${cursor ?? ''}`
    : null;
  const read = useAdminRead(key, (signal) =>
    api.listAudit(
      {
        ...(scopeOrganization ? { organizationId: scopeOrganization } : {}),
        ...(applied.action.trim() ? { action: applied.action.trim() } : {}),
        ...(applied.actor.trim() ? { actorId: applied.actor.trim() } : {}),
        ...(applied.from ? { from: startOfDay(applied.from) } : {}),
        ...(applied.to ? { to: endOfDay(applied.to) } : {}),
        ...(cursor ? { cursor } : {}),
        limit: AUDIT_PAGE_SIZE,
      },
      signal,
    ),
  );
  const current = read.dataKey === key ? read.data : null;
  const shown = read.failure ? null : read.data;
  const filtered = appliedSearch !== '';

  const apply = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const found = validateFilters(draft);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    void navigate({ search: auditSearch(draft) }, { replace: true, state: carried });
  };
  const goPage = (nextCursor: string | undefined, nextPrevious: string[] | null) =>
    void navigate(
      { search: auditSearch(applied, nextCursor) },
      { state: { ...carried, ...(nextPrevious ? { previous: nextPrevious } : {}) } },
    );

  let statusText = '';
  if (read.loading) statusText = 'Loading audit events';
  else if (current) {
    statusText = current.items.length === 0 ? '' : plural(current.items.length, 'event', 'events');
    if (statusText && pageNumber && (pageNumber > 1 || current.nextCursor))
      statusText += `, page ${formatCount(pageNumber)}`;
  }

  let results: ReactNode;
  if (!valid) {
    results = (
      <EmptyPageState
        icon={<NavigationGlyph name="Search" />}
        title="These filters cannot be used"
        description="Correct the highlighted filters, then apply them again."
        action={<ActionLink to={{ search: '' }}>Clear the filters</ActionLink>}
      />
    );
  } else if (read.failure) {
    results = (
      <AdminProblem
        failure={read.failure}
        subject="the audit log"
        onRetry={read.retry}
        supportAccess={supportAccess}
        action={
          read.failure.kind === 'invalid-request' ? (
            <ActionLink primary to={{ search: appliedSearch }}>
              Go to the first page
            </ActionLink>
          ) : undefined
        }
      />
    );
  } else if (shown && shown.items.length > 0) {
    results = (
      <AuditTable
        events={shown.items}
        busy={read.loading}
        caption={pageNumber ? `Audit events, page ${formatCount(pageNumber)}` : 'Audit events'}
        showOrganization={global}
      />
    );
  } else if (current) {
    results = filtered ? (
      <EmptyPageState
        icon={<NavigationGlyph name="Search" />}
        title="No events match these filters"
        description="Actions are matched exactly; dates are whole days in UTC."
        action={<ActionLink to={{ search: '' }}>Clear the filters</ActionLink>}
      />
    ) : cursor ? (
      <EmptyPageState
        icon={<NavigationGlyph name="ShieldCheck" />}
        title="No more events"
        description="This page is past the end of the log."
        action={<ActionLink to={{ search: '' }}>Go to the first page</ActionLink>}
      />
    ) : (
      <EmptyPageState
        icon={<NavigationGlyph name="ShieldCheck" />}
        title="No audit events yet"
        description="Changes and staff reads appear here as they happen."
      />
    );
  } else {
    results = <LoadingBlock label="Loading the audit log" />;
  }

  const nextCursor = current?.nextCursor ?? null;
  const showPager = !read.failure && shown !== null && (cursor !== undefined || shown.nextCursor !== null);
  const fieldErrors = { ...appliedErrors, ...errors };

  return (
    <div className="admin-list">
      <form
        className="admin-filters admin-filters--audit"
        onSubmit={apply}
        noValidate
        aria-label="Audit filters"
      >
        <TextField
          label="Action"
          value={draft.action}
          onChange={(value) => setDraft((d) => ({ ...d, action: value }))}
          hint="The exact action, for example module.configuration.changed."
          error={fieldErrors.action ?? null}
          maxLength={100}
          spellCheck={false}
        />
        <TextField
          label="Actor ID"
          value={draft.actor}
          onChange={(value) => setDraft((d) => ({ ...d, actor: value }))}
          error={fieldErrors.actor ?? null}
          maxLength={100}
          spellCheck={false}
        />
        <TextField
          label="From"
          type="date"
          value={draft.from}
          onChange={(value) => setDraft((d) => ({ ...d, from: value }))}
          error={fieldErrors.from ?? null}
        />
        <TextField
          label="To"
          type="date"
          value={draft.to}
          onChange={(value) => setDraft((d) => ({ ...d, to: value }))}
          error={fieldErrors.to ?? null}
        />
        {global ? (
          <TextField
            label="Organization ID"
            value={draft.organization}
            onChange={(value) => setDraft((d) => ({ ...d, organization: value }))}
            error={fieldErrors.organization ?? null}
            spellCheck={false}
          />
        ) : null}
        <div className="admin-form__actions">
          <button type="submit" className="admin-action admin-action--primary">
            Apply the filters
          </button>
          {filtered ? <ActionLink to={{ search: '' }}>Clear the filters</ActionLink> : null}
        </div>
      </form>
      <p className="admin-results-status" role="status" aria-live="polite">
        {statusText}
      </p>
      {results}
      {showPager ? (
        <div className="admin-pager">
          {previous ? (
            <PagerButton
              onClick={() => {
                if (!previous || previous.length === 0) return;
                const before = previous[previous.length - 1];
                goPage(before || undefined, previous.slice(0, -1));
              }}
              disabled={previous.length === 0 || read.loading}
            >
              Previous page
            </PagerButton>
          ) : (
            <PagerButton onClick={() => goPage(undefined, null)} disabled={read.loading}>
              First page
            </PagerButton>
          )}
          <PagerButton
            onClick={() => {
              if (nextCursor) goPage(nextCursor, previous ? [...previous, cursor ?? ''] : null);
            }}
            disabled={nextCursor === null || read.loading}
          >
            Next page
          </PagerButton>
        </div>
      ) : null}
    </div>
  );
}
